import React, {useState, useRef} from "react"
import {
    Avatar, Box, CircularProgress, Popover, List, ListItemButton,
    ListItemIcon, ListItemText, Divider, Typography,
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, Snackbar, Alert,
} from "@mui/material"
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera"
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline"
import EditIcon from "@mui/icons-material/Edit"
import {useTranslation} from "react-i18next"
import {GetLogo, UploadLogo, DeleteLogo} from "../../profile/main/api/photo-data-handler"
import {GetTenantData} from "../../profile/main/api/get-tenant-data"

const PLACEHOLDER = "/placeholder.svg?height=120&width=120"

const LogoUploader = ({tenantId, logoUrl, hasLogo, onUpdate, size = 120}) => {
    const {t} = useTranslation()
    const fileInputRef = useRef(null)
    const avatarRef = useRef(null)

    const [popoverOpen, setPopoverOpen]     = useState(false)
    const [confirmOpen, setConfirmOpen]     = useState(false)
    const [uploading, setUploading]         = useState(false)
    const [removing, setRemoving]           = useState(false)
    const [snack, setSnack]                 = useState({open: false, message: "", severity: "success"})

    const busy = uploading || removing
    const showSnack = (message, severity = "success") => setSnack({open: true, message, severity})

    const handleAvatarClick = () => {
        if (!busy) setPopoverOpen(true)
    }

    const handleUploadClick = () => {
        setPopoverOpen(false)
        // small delay so popover fully closes before file dialog opens
        setTimeout(() => fileInputRef.current?.click(), 80)
    }

    const handleRemoveClick = () => {
        setPopoverOpen(false)
        setTimeout(() => setConfirmOpen(true), 80)
    }

    const handleFileChange = async (e) => {
        const file = e.target.files[0]
        if (!file) return
        e.target.value = ""
        setUploading(true)
        try {
            await UploadLogo({id: tenantId, file})
            const updatedTenant = await GetTenantData({tenantId})
            const newUrl = updatedTenant?.imageUrl
                ? await GetLogo({filename: updatedTenant.imageUrl})
                : PLACEHOLDER
            onUpdate(updatedTenant, newUrl || PLACEHOLDER)
            showSnack(t("LogoUploadedSuccessfully"))
        } catch (err) {
            showSnack(err.response?.data?.message || t("LogoUploadFailed"), "error")
        } finally {
            setUploading(false)
        }
    }

    const handleConfirmRemove = async () => {
        setConfirmOpen(false)
        setRemoving(true)
        try {
            await DeleteLogo({id: tenantId})
            const updatedTenant = await GetTenantData({tenantId})
            onUpdate(updatedTenant, PLACEHOLDER)
            showSnack(t("LogoRemovedSuccessfully"))
        } catch (err) {
            showSnack(err.response?.data?.message || t("LogoRemoveFailed"), "error")
        } finally {
            setRemoving(false)
        }
    }

    return (
        <>
            {/* ── Avatar — clickable ─────────────────────────────────────── */}
            <Box
                component="button"
                type="button"
                aria-label={t("WorkspaceManageLogo")}
                ref={avatarRef}
                onClick={handleAvatarClick}
                sx={{
                    position: "relative",
                    border: 0, p: 0, bgcolor: "transparent",
                    width: size,
                    height: size,
                    cursor: busy ? "default" : "pointer",
                    borderRadius: "50%",
                    // subtle ring on hover
                    "&:hover .logo-overlay": {opacity: busy ? 0 : 1},
                }}
            >
                <Avatar
                    src={logoUrl}
                    sx={{
                        width: size,
                        height: size,
                        boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
                        opacity: busy ? 0.55 : 1,
                        transition: "opacity 0.2s",
                    }}
                />

                {/* Hover overlay */}
                <Box
                    className="logo-overlay"
                    sx={{
                        position: "absolute", inset: 0,
                        borderRadius: "50%",
                        bgcolor: "rgba(0,0,0,0.38)",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        opacity: 0,
                        transition: "opacity 0.18s",
                        pointerEvents: "none",
                    }}
                >
                    <EditIcon sx={{color: "white", fontSize: size * 0.28}}/>
                </Box>

                {/* Spinner while busy */}
                {busy && (
                    <Box sx={{
                        position: "absolute", inset: 0,
                        display: "flex", alignItems: "center", justifyContent: "center",
                        borderRadius: "50%",
                    }}>
                        <CircularProgress size={size * 0.35} thickness={3.5}/>
                    </Box>
                )}
            </Box>

            {/* Hidden file input */}
            <input
                ref={fileInputRef}
                hidden
                type="file"
                accept="image/png,image/jpeg,image/webp,image/svg+xml"
                onChange={handleFileChange}
            />

            {/* ── Popover menu ───────────────────────────────────────────── */}
            <Popover
                open={popoverOpen}
                anchorEl={avatarRef.current}
                onClose={() => setPopoverOpen(false)}
                anchorOrigin={{vertical: "bottom", horizontal: "center"}}
                transformOrigin={{vertical: "top", horizontal: "center"}}
                slotProps={{
                    paper: {
                        sx: {
                            mt: 1,
                            borderRadius: 2.5,
                            boxShadow: "0 8px 24px rgba(0,0,0,0.14)",
                            minWidth: 200,
                            overflow: "hidden",
                        }
                    }
                }}
            >
                {/* Small preview + label at the top */}
                <Box sx={{
                    px: 2, pt: 1.5, pb: 1,
                    display: "flex", alignItems: "center", gap: 1.5,
                }}>
                    <Avatar src={logoUrl} sx={{width: 36, height: 36}}/>
                    <Typography variant="body2" fontWeight={600} color="text.primary">
                        {t("Logo")}
                    </Typography>
                </Box>

                <Divider/>

                <List dense disablePadding>
                    {/* Upload / Change */}
                    <ListItemButton
                        onClick={handleUploadClick}
                        sx={{"&:hover": {bgcolor: "primary.50"}}}
                    >
                        <ListItemIcon sx={{minWidth: 36}}>
                            <PhotoCameraIcon fontSize="small" color="primary"/>
                        </ListItemIcon>
                        <ListItemText
                            primary={hasLogo ? t("ChangeLogo") : t("UploadLogo")}
                            primaryTypographyProps={{variant: "body2"}}
                        />
                    </ListItemButton>

                    {/* Remove — only when a logo exists */}
                    {hasLogo && (
                        <ListItemButton
                            onClick={handleRemoveClick}
                            sx={{"&:hover": {bgcolor: "error.50"}}}
                        >
                            <ListItemIcon sx={{minWidth: 36}}>
                                <DeleteOutlineIcon fontSize="small" color="error"/>
                            </ListItemIcon>
                            <ListItemText
                                primary={t("RemoveLogo")}
                                primaryTypographyProps={{variant: "body2", color: "error.main"}}
                            />
                        </ListItemButton>
                    )}
                </List>
            </Popover>

            {/* ── Confirm remove dialog ──────────────────────────────────── */}
            <Dialog
                open={confirmOpen}
                onClose={() => setConfirmOpen(false)}
                maxWidth="xs"
                fullWidth
                PaperProps={{sx: {borderRadius: 3}}}
            >
                <DialogTitle sx={{pb: 1}}>{t("RemoveLogo")}</DialogTitle>
                <DialogContent>
                    <Box sx={{display: "flex", gap: 2, alignItems: "center"}}>
                        <Avatar src={logoUrl} sx={{width: 56, height: 56, flexShrink: 0}}/>
                        <Typography variant="body2" color="text.secondary">
                            {t("RemoveLogoConfirmMessage")}
                        </Typography>
                    </Box>
                </DialogContent>
                <DialogActions sx={{px: 3, pb: 2.5, gap: 1}}>
                    <Button
                        variant="outlined"
                        onClick={() => setConfirmOpen(false)}
                        sx={{borderRadius: 2}}
                    >
                        {t("Cancel")}
                    </Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={handleConfirmRemove}
                        startIcon={<DeleteOutlineIcon/>}
                        sx={{borderRadius: 2}}
                    >
                        {t("Remove")}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ── Snackbar ───────────────────────────────────────────────── */}
            <Snackbar
                open={snack.open}
                autoHideDuration={4000}
                onClose={() => setSnack(s => ({...s, open: false}))}
                anchorOrigin={{vertical: "bottom", horizontal: "right"}}
            >
                <Alert
                    onClose={() => setSnack(s => ({...s, open: false}))}
                    severity={snack.severity}
                    variant="filled"
                    sx={{borderRadius: 2}}
                >
                    {snack.message}
                </Alert>
            </Snackbar>
        </>
    )
}

export default LogoUploader
